// App.tsx

import "@fontsource/roboto/700.css";

import { useEffect, useMemo, useState, type JSX } from "react";
import { Routes, Route, Navigate, useLocation, useSearchParams, useParams, Outlet } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { enUS, zhTW, zhCN } from "@mui/material/locale";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Container, useMediaQuery } from "@mui/material";
import useTranslatedRoutes from "./routes";
import DefaultNavbar from "./navbars/DefaultNavbar";
import DefaultFooter from "./navbars/DefaultFooter";
import { getLightTheme, getDarkTheme } from "./theme";
import type { NavbarRoute } from "./interfaces";
import { useTranslation } from "react-i18next";
import { getSavedLanguage } from "./i18n/languagePersistence";
import { SnackbarProvider } from "notistack";
import LoginPage from "./pages/LoginPage";
import { FloatingDisclaimer } from "./components/FloatingDisclaimer";
import { ProtectedRoute } from "./components/ProtectedRoutes";
import { useAuthStore } from "./store/useAuthStore";
import { LandingDashboardPage } from "./pages/LandingDashboardPage";
import { SocketProvider } from "./context/SocketProvider";
import { GlobalNotificationListener } from "./components/NotificationHeader";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/types/Languages.types";

const DEFAULT_LANGUAGE: SupportedLanguage = "zh-hant";

// Map app language codes to MUI core locale objects
const muiLocales = {
  "en-us": enUS,
  "zh-hant": zhTW,
  "zh-hans": zhCN,
};

function RedirectToDefaultLang() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const activeLang = (i18n.language || getSavedLanguage()) ?? DEFAULT_LANGUAGE;
  const targetPath = `/${activeLang}${location.pathname === "/" ? "" : location.pathname}`;
  return <Navigate to={targetPath + location.search} replace />;
}

function LanguageWrapper() {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();

  // const resolvedLang = lang ?? getSavedLanguage() ?? DEFAULT_LANGUAGE;
  // const isSupported = (SUPPORTED_LANGUAGES as readonly string[]).includes(resolvedLang);
  // const currentLang = isSupported ? (resolvedLang as SupportedLanguage) : DEFAULT_LANGUAGE;

  // useEffect(() => {
  //   if (currentLang && i18n.language !== currentLang) {
  //     i18n.changeLanguage(currentLang);
  //   }
  // }, [currentLang, i18n]);

  if (lang && !(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
    return <Navigate to={`/${i18n.language || DEFAULT_LANGUAGE}`} replace />;
  }

  return <Outlet />;
}

export default function App() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [isDarkMode] = useState<boolean>(false);
  const { i18n } = useTranslation();
  const dynamicRoutes = useTranslatedRoutes();

  const [isNavbarMinimized, setIsNavbarMinimized] = useState<boolean>(false);
  const isLoginPage = pathname.includes("/login");
  const MAX_DRAWER_WIDTH = "260px";
  const drawerWidth = isNavbarMinimized ? "70px" : MAX_DRAWER_WIDTH;
  const mobileNavHeight = "64px";

  const user = useAuthStore((state) => state.user);
  const initAuth = useAuthStore((state) => state.initAuth);

  useEffect(() => {
    const unsubscribe = initAuth();
    return () => unsubscribe();
  }, [initAuth]);

  // Dynamically merge MUI component locale strings into base light/dark theme
  const currentTheme = useMemo(() => {
    const baseTheme = isDarkMode ? getDarkTheme() : getLightTheme();
    const activeLang = (i18n.language?.toLowerCase() as SupportedLanguage) || DEFAULT_LANGUAGE;
    const muiLocale = muiLocales[activeLang] || zhTW;

    return createTheme(baseTheme, muiLocale);
  }, [isDarkMode, i18n.language]);

  const isMdDown = useMediaQuery(currentTheme.breakpoints.down("md"));
  const isMobileMode = searchParams.get("mobile") === "true" || isMdDown;

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  const getRoutes = (allRoutes: NavbarRoute[]): JSX.Element[] =>
    allRoutes
      .map((route: NavbarRoute) => {
        let basedRoutes: JSX.Element[] = [];
        if (route.route && route.component) {
          basedRoutes.push(<Route path={route.route} element={route.component} key={route.key?.toString()} />);
        }
        if (route.collapse) {
          basedRoutes = [...basedRoutes, ...getRoutes(route.collapse)];
        }
        return basedRoutes;
      })
      .flat();

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />

      {/* 1. MAIN CONTENT CONTAINER */}
      <Box
        sx={{
          pl: isMobileMode || isLoginPage ? 0 : drawerWidth,
          pt: isMobileMode && !isLoginPage ? mobileNavHeight : 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          transition: "padding-left 0.2s ease-in-out, padding-top 0.2s ease-in-out",
        }}
      >
        <SnackbarProvider
          maxSnack={3}
          autoHideDuration={1000}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <SocketProvider userId={user?.userId ?? ""}>
            <GlobalNotificationListener />
            <Box component="main" sx={{ flexGrow: 1, width: "100%" }}>
              <Container
                maxWidth={isLoginPage ? false : "xl"}
                disableGutters={isLoginPage}
                sx={{
                  mt: isLoginPage ? 0 : 4,
                  mb: isLoginPage ? 0 : 4,
                  px: isLoginPage ? 0 : { xs: 2, sm: 3, md: 4 },
                  height: isLoginPage ? "100vh" : "auto",
                }}
              >
                <Routes>
                  <Route path="/" element={<RedirectToDefaultLang />} />
                  <Route path="/:lang" element={<LanguageWrapper />}>
                    {/* Public Unprotected Route */}
                    <Route path="login" element={<LoginPage />} />

                    {/* Protected Routes Guard */}
                    <Route element={<ProtectedRoute />}>
                      {getRoutes(dynamicRoutes)}
                      <Route index element={<LandingDashboardPage />} />
                    </Route>

                    <Route path="*" element={<Navigate to="." replace />} />
                  </Route>
                  <Route path="*" element={<RedirectToDefaultLang />} />
                </Routes>
              </Container>
            </Box>
          </SocketProvider>
        </SnackbarProvider>

        {!isLoginPage && (
          <Box component="footer" sx={{ width: "100%", mt: "auto" }}>
            <DefaultFooter />
          </Box>
        )}
      </Box>

      {/* 2. RESPONSIVE SIDEBAR */}
      {!isLoginPage && (
        <Box
          sx={
            isMobileMode
              ? {
                  position: "fixed",
                  zIndex: 1100,
                  top: 0,
                  left: 0,
                  right: 0,
                  height: mobileNavHeight,
                  borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: "background.paper",
                  transition: "all 0.2s ease-in-out",
                }
              : {
                  position: "fixed",
                  zIndex: 1100,
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: drawerWidth,
                  borderRight: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: "background.paper",
                  transition: "width 0.2s ease-in-out",
                }
          }
        >
          <DefaultNavbar
            routes={dynamicRoutes}
            onMinimizeToggle={(_isNavbarMinimized) => setIsNavbarMinimized(_isNavbarMinimized)}
          />
        </Box>
      )}

      <FloatingDisclaimer />
    </ThemeProvider>
  );
}
